import { createSelector } from '@reduxjs/toolkit';
import type { AppState } from 'admin/src/store';
import type { AvailabilitySurveyState } from 'admin/src/reducers/pollworker/availabilitySurvey';
import dayjs from 'dayjs';

export interface SurveyWorkerRow {
  evUserId: string;
  name: string;
  email: string;
  partyCode: string;
  earlyVotingWorker: boolean;
  electionDayWorker: boolean;
  responses: Map<string, { resultId: string; shiftAvailable: number }>;
  availableCount: number;
  totalCount: number;
}

export const selectAvailabilitySurveySlice = (state: AppState): AvailabilitySurveyState => state.pollworker.availabilitySurvey;

export const selectSurveyId = createSelector(selectAvailabilitySurveySlice, (s) => s.surveyId);

export const selectSurveyElectionId = createSelector(selectAvailabilitySurveySlice, (s) => s.electionId);

export const selectSurveyData = createSelector(selectAvailabilitySurveySlice, (s) => s.data);

export const selectSurveyIsLoading = createSelector(selectAvailabilitySurveySlice, (s) => s.isLoading);

export const selectSurveyError = createSelector(selectAvailabilitySurveySlice, (s) => s.error);

export const selectSurveyNotFound = createSelector(selectAvailabilitySurveySlice, (s) => s.surveyNotFound);

export const selectUpdatingResultIds = createSelector(selectAvailabilitySurveySlice, (s) => new Set(s.updatingResultIds));

export const selectRemovingWorkerIds = createSelector(selectAvailabilitySurveySlice, (s) => new Set(s.removingWorkerIds));

export const selectAddingDateKeys = createSelector(selectAvailabilitySurveySlice, (s) => new Set(s.addingDateKeys));

export const selectDeletingDateKeys = createSelector(selectAvailabilitySurveySlice, (s) => new Set(s.deletingDateKeys));

export const selectBatchUpdatingWorkerIds = createSelector(selectAvailabilitySurveySlice, (s) => new Set(s.batchUpdatingWorkerIds));

export const selectSurveyWorkerRows = createSelector(selectSurveyData, (data): SurveyWorkerRow[] => {
  if (!data?.workers) return [];

  const workerMap = new Map<string, SurveyWorkerRow>();

  for (const worker of data.workers) {
    let row = workerMap.get(worker.evUserId);
    if (!row) {
      row = {
        evUserId: worker.evUserId,
        name: worker.name,
        email: worker.email,
        partyCode: worker.partyCode,
        earlyVotingWorker: worker.earlyVotingWorker,
        electionDayWorker: worker.electionDayWorker,
        responses: new Map(),
        availableCount: 0,
        totalCount: 0,
      };
      workerMap.set(worker.evUserId, row);
    }

    const dateKey = dayjs.utc(worker.dateAvailable).format('YYYY-MM-DD');
    row.responses.set(dateKey, {
      resultId: worker.resultId,
      shiftAvailable: worker.shiftAvailable,
    });
    row.totalCount++;
    if (worker.shiftAvailable === 1) {
      row.availableCount++;
    }
  }

  return Array.from(workerMap.values());
});

export const selectWorkersWithPendingResponses = createSelector(selectSurveyWorkerRows, (workers) =>
  workers.filter((w) => Array.from(w.responses.values()).some((r) => r.shiftAvailable === 2)),
);

export const selectSurveyDates = createSelector(selectSurveyData, (data) => data?.dates ?? []);

export const selectSurveyElectionDate = createSelector(selectSurveyData, (data) => data?.electionDate ?? null);
